import { useEffect, useMemo, useState } from 'react';
import { useUserStore } from '@repo/front-end-state';
import { fetchModelFootprint, type ModelFootprintFetchResult } from '@repo/front-end-utils';
import { getModelURL } from '../../components/3D/secureRemoteFiles';
import { modelFootprintKey, type MachineFootprints } from './calibrationModel';

/**
 * The calibration screen's machine footprints, read from each model file's header
 * (`onboarding-3d-plan-footprint-calibration`, R3).
 *
 * - **Once per model per page load.** Reads are shared through a module-level cache keyed by
 *   `modelFootprintKey`, so a second screen — or a second camera's — reads nothing new and two
 *   spellings of one model are one read. A failed read leaves the cache, so reopening the screen
 *   retries it. A model re-uploaded during the page load is not re-read until reload: the bound the
 *   live twin's per-load cache-bust already sets.
 * - **The live twin's address** (`getModelURL`: the user's model token, the per-load cache-bust),
 *   with the token read when the read starts.
 * - **Cancellation is shared.** Each read has its own controller and a count of the screens
 *   waiting on it; it is aborted only when the last one has unmounted and none has come back by
 *   the next tick, so one screen closing never cancels another's read.
 * - **At most `MAX_CONCURRENT_FOOTPRINT_READS` at once**, so each read's 30 s deadline measures its
 *   own request rather than time spent queued behind the browser's per-host connection limit.
 * - **Logged once per model and reason per page load** when a read fails, by name, reason and (for
 *   an HTTP error) status — never the address, which carries the model token.
 */

type CachedRead = {
  result: Promise<ModelFootprintFetchResult>;
  controller: AbortController;
  waiting: number;
  settled: boolean;
};

/** Header reads in flight at once: the browser's per-host connection limit over HTTP/1.1. */
export const MAX_CONCURRENT_FOOTPRINT_READS = 6;

const reads = new Map<string, CachedRead>();
const warned = new Set<string>();
let inFlight = 0;
const waitingForSlot: Array<() => void> = [];

const acquireSlot = (): Promise<void> => {
  if (inFlight < MAX_CONCURRENT_FOOTPRINT_READS) {
    inFlight += 1;
    return Promise.resolve();
  }
  return new Promise(resolve =>
    waitingForSlot.push(() => {
      inFlight += 1;
      resolve();
    }),
  );
};

const releaseSlot = () => {
  inFlight -= 1;
  waitingForSlot.shift()?.();
};

/** Clears the page-load cache, the warned set and the read slots. For tests only. */
export function resetModelFootprintCacheForTests(): void {
  reads.clear();
  warned.clear();
  inFlight = 0;
  waitingForSlot.length = 0;
}

/** One read, once a slot is free; a read cancelled while it queued is never sent. */
async function readWhenFree(reference: string, signal: AbortSignal) {
  await acquireSlot();
  try {
    if (signal.aborted) return { ok: false, reason: 'network' } as const;
    return await fetchModelFootprint(getModelURL(reference, useUserStore.getState().authMetadata), {
      signal,
    });
  } finally {
    releaseSlot();
  }
}

function startRead(key: string, reference: string): CachedRead {
  const controller = new AbortController();
  const entry: CachedRead = {
    controller,
    waiting: 0,
    settled: false,
    result: readWhenFree(reference, controller.signal).then((result): ModelFootprintFetchResult => {
      entry.settled = true;
      if (!result.ok) {
        // Failures are not cached: the next screen to ask reads again.
        if (reads.get(key) === entry) reads.delete(key);
        // Once per model AND reason, so a later, different cause (an expired token) is logged.
        const once = `${key}|${result.reason}`;
        if (!controller.signal.aborted && !warned.has(once)) {
          warned.add(once);
          const detail = 'status' in result && result.status ? [result.status] : [];
          console.warn(
            '[calibration] model footprint unavailable',
            reference,
            result.reason,
            ...detail,
          );
        }
      }
      return result;
    }),
  };
  reads.set(key, entry);
  return entry;
}

/**
 * The hook's answer: `settled` and `footprints` as the calibration overlay reads them (every model
 * answered, or nothing), and `arrived`, each model's result as soon as its own read settles, for a
 * reader that measures machine by machine (the plan's bounds, `onboarding-3d-plan-konva-removal`
 * R5). Once settled, `arrived` is `footprints`.
 */
export interface ModelFootprintsRead extends MachineFootprints {
  arrived: MachineFootprints['footprints'];
}

export function useModelFootprints(
  modelFiles: ReadonlyArray<string | null | undefined>,
): ModelFootprintsRead {
  // The first reference seen for each key is the one its address is built from.
  const wanted = useMemo(() => {
    const byKey = new Map<string, string>();
    for (const reference of modelFiles) {
      const key = modelFootprintKey(reference);
      if (key && !byKey.has(key)) byKey.set(key, reference!.trim());
    }
    return byKey;
  }, [modelFiles]);
  const signature = useMemo(() => Array.from(wanted.keys()).sort().join('\n'), [wanted]);

  const [state, setState] = useState<{
    signature: string;
    footprints: MachineFootprints['footprints'];
  }>();
  const [arrived, setArrived] = useState<{
    signature: string;
    footprints: Map<string, ModelFootprintFetchResult>;
  }>();

  useEffect(() => {
    let current = true;
    const entries = Array.from(wanted).map(([key, reference]) => {
      const entry = reads.get(key) ?? startRead(key, reference);
      entry.waiting += 1;
      // Each result as its own read settles; a cached one lands on the next microtask.
      entry.result.then(result => {
        if (!current) return;
        setArrived(prev => ({
          signature,
          footprints: new Map(prev?.signature === signature ? prev.footprints : []).set(
            key,
            result,
          ),
        }));
      });
      return [key, entry] as const;
    });
    Promise.all(entries.map(([key, entry]) => entry.result.then(r => [key, r] as const))).then(
      results => {
        if (current) setState({ signature, footprints: new Map(results) });
      },
    );
    return () => {
      current = false;
      for (const [key, entry] of entries) {
        entry.waiting -= 1;
        // A tick later, so a screen that remounts at once (React's development double mount, or
        // a re-render that changes the model list and back) picks the read up instead of
        // cancelling it and starting another.
        setTimeout(() => {
          if (entry.waiting === 0 && !entry.settled) {
            entry.controller.abort();
            if (reads.get(key) === entry) reads.delete(key);
          }
        }, 0);
      }
    };
    // `signature` names exactly the keys in `wanted`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  return useMemo(() => {
    if (state?.signature === signature) {
      return { settled: true, footprints: state.footprints, arrived: state.footprints };
    }
    const none = new Map<string, ModelFootprintFetchResult>();
    return {
      settled: signature === '',
      footprints: none,
      arrived: arrived?.signature === signature ? arrived.footprints : none,
    };
  }, [state, arrived, signature]);
}
